import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';

import { ILocalization, withLocalization } from '@shared-ui/localization-context';

import { UitkIcon } from 'uitk-react-icons';
import { UitkLink } from 'uitk-react-link';
import { UitkText } from 'uitk-react-text';

import { DateUtils } from 'utils/date-utils';

const MAXIMUM_DATE_SPAN = 28;
const NUM_OF_DAYS_BOOKABLE_IN_ADVANCE = 500;

const LinkTracked = ClickTracker(UitkLink);

export interface StepperButtonProps {
  clickHandler?: (event: any, isTriggeredByStepper: boolean) => void;
  endDate: string;
  l10n?: ILocalization;
  linkName: string;
  rfrr: string;
  startDate: string;
  stepperType: string;
}

export interface StepperButtonState {
  mounted: boolean;
}

@withLocalization
export class StepperButton extends React.Component<StepperButtonProps, StepperButtonState> {
  private dateUtils: DateUtils;

  constructor(props: any) {
    super(props);

    this.stepDates = this.stepDates.bind(this);

    this.state = {
      mounted: false,
    };

    this.dateUtils = new DateUtils(props.l10n);
  }

  public static defaultProps = {
    iconName: '',
    l10n: null,
  };

  public stepDates() {
    if (this.props.clickHandler && typeof this.props.clickHandler === 'function') {
      this.props.clickHandler(this.determineStepDate(), true);
    }
  }

  public determineIfDisabled() {
    const determineStepDateResult = this.props.startDate && this.props.endDate && this.determineStepDate();

    return !this.state.mounted || !determineStepDateResult;
  }

  public determineStepDate() {
    const { dateUtils } = this;

    const { endDate, startDate, stepperType } = this.props;
    const currentCheckInDate = startDate;
    const currentCheckOutDate = endDate;
    const target: any[] = [];
    const stepDateEvent = {
      target,
    };
    let stepCheckInDate;
    let stepCheckOutDate;
    switch (stepperType) {
      case 'CHECK_IN_MINUS':
        stepCheckInDate = dateUtils.getISODayBefore(currentCheckInDate);
        if (DateUtils.isBefore(stepCheckInDate, dateUtils.getToday())) {
          stepCheckInDate = undefined;
        } else if (dateUtils.getDaysBetween(stepCheckInDate, currentCheckOutDate) >= MAXIMUM_DATE_SPAN) {
          stepCheckInDate = undefined;
        }
        break;
      case 'CHECK_IN_PLUS':
        stepCheckInDate = dateUtils.getISODayAfter(currentCheckInDate);
        if (dateUtils.getDaysBetween(dateUtils.getToday(), stepCheckInDate) >= NUM_OF_DAYS_BOOKABLE_IN_ADVANCE) {
          stepCheckInDate = undefined;
        } else if (dateUtils.isSame(stepCheckInDate, currentCheckOutDate)) {
          stepCheckOutDate = dateUtils.getISODayAfter(currentCheckOutDate);
        } else if (dateUtils.getDaysBetween(currentCheckInDate, currentCheckOutDate) >= MAXIMUM_DATE_SPAN) {
          stepCheckInDate = dateUtils.getNDaysFrom(currentCheckOutDate, -(MAXIMUM_DATE_SPAN - 1));
        }
        break;
      case 'CHECK_OUT_MINUS':
        stepCheckOutDate = dateUtils.getISODayBefore(currentCheckOutDate);
        if (DateUtils.isBefore(stepCheckOutDate, dateUtils.getISODayAfter(dateUtils.getToday()))) {
          stepCheckOutDate = undefined;
        } else if (dateUtils.isSame(stepCheckOutDate, currentCheckInDate)) {
          stepCheckInDate = dateUtils.getISODayBefore(currentCheckInDate);
        } else if (dateUtils.getDaysBetween(currentCheckInDate, currentCheckOutDate) >= MAXIMUM_DATE_SPAN) {
          stepCheckOutDate = dateUtils.getNDaysFrom(currentCheckInDate, MAXIMUM_DATE_SPAN - 1);
        }
        break;
      case 'CHECK_OUT_PLUS':
        stepCheckOutDate = dateUtils.getISODayAfter(currentCheckOutDate);
        if (dateUtils.getDaysBetween(dateUtils.getToday(), stepCheckOutDate) > NUM_OF_DAYS_BOOKABLE_IN_ADVANCE) {
          stepCheckOutDate = undefined;
        } else if (dateUtils.getDaysBetween(currentCheckInDate, stepCheckOutDate) >= MAXIMUM_DATE_SPAN) {
          stepCheckOutDate = undefined;
        }
        break;
      default:
        stepCheckInDate = undefined;
        stepCheckOutDate = undefined;
        break;
    }

    if (!stepCheckInDate && !stepCheckOutDate) {
      return false;
    }

    if (stepCheckInDate) {
      stepDateEvent.target.push({
        name: 'startDate',
        value: stepCheckInDate,
      });
    }

    if (stepCheckOutDate) {
      stepDateEvent.target.push({
        name: 'endDate',
        value: stepCheckOutDate,
      });
    }

    return stepDateEvent;
  }

  public componentDidMount() {
    this.setState({
      mounted: true,
    });
  }

  public render() {
    const { l10n, linkName, rfrr, stepperType } = this.props;
    let iconName = '';
    let identifier = 'date-stepper-button';
    let ariaLabelKey = '';
    let align = 'left';
    switch (stepperType) {
      case 'CHECK_IN_MINUS':
        identifier += '-check-in-previous';
        iconName = 'chevron_left';
        ariaLabelKey = 'hotels.infosite.availabilityWizard.changeDate.checkIn.decrease.a11yLabel';
        break;
      case 'CHECK_OUT_MINUS':
        identifier += '-check-out-previous';
        iconName = 'chevron_left';
        ariaLabelKey = 'hotels.infosite.availabilityWizard.changeDate.checkOut.decrease.a11yLabel';
        break;
      case 'CHECK_IN_PLUS':
        align = 'right';
        identifier += '-check-in-next';
        iconName = 'chevron_right';
        ariaLabelKey = 'hotels.infosite.availabilityWizard.changeDate.checkIn.increase.a11yLabel';
        break;
      case 'CHECK_OUT_PLUS':
        align = 'right';
        identifier += '-check-out-next';
        iconName = 'chevron_right';
        ariaLabelKey = 'hotels.infosite.availabilityWizard.changeDate.checkOut.increase.a11yLabel';
        break;
      default:
        break;
    }

    return (
      <LinkTracked
        data-stid={identifier}
        align={align}
        linkName={linkName}
        disabled={this.determineIfDisabled()}
        onClick={this.stepDates}
        rfrr={rfrr}
        aria-label={l10n?.formatText(ariaLabelKey)}
      >
        <button>
          <UitkText align="center">
            <UitkIcon name={iconName} />
          </UitkText>
        </button>
      </LinkTracked>
    );
  }
}
